<template>
  <HrPageLayout
    title="Assets Management"
    subtitle="Track and manage company assets"
    icon="🖥️"
    :breadcrumb-items="[{ label: 'HR Management' }, { label: 'Assets' }]"
  >
    <template #header-actions>
      <button @click="refreshData" class="hr-btn hr-btn-secondary">
        <i class="mdi mdi-refresh"></i> Refresh
      </button>
    </template>

    <div class="container-fluid">
      <!-- Statistics cards -->
            <div class="row">
              <!-- Total Assets -->
              <div class="col-xl-3 col-md-6 mb-4">
                <div class="card border-left-primary h-100 py-2">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-shrink-0 me-3">
                        <div class="avatar-sm">
                          <span class="avatar-title bg-primary rounded-circle text-white font-size-20">
                            <i class="mdi mdi-desktop-mac font-size-24"></i>
                          </span>
                        </div>
                      </div>
                      <div class="flex-grow-1">
                        <h6 class="font-size-15 mb-1">Total Assets</h6>
                        <h4 class="mb-0">{{ metrics.totalAssets || 0 }}</h4>
                        <span class="text-muted font-size-12">
                          Overall inventory
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Assigned Assets -->
              <div class="col-xl-3 col-md-6 mb-4">
                <div class="card border-left-success h-100 py-2">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-shrink-0 me-3">
                        <div class="avatar-sm">
                          <span class="avatar-title bg-success rounded-circle text-white font-size-20">
                            <i class="mdi mdi-account-check font-size-24"></i>
                          </span>
                        </div>
                      </div>
                      <div class="flex-grow-1">
                        <h6 class="font-size-15 mb-1">Assigned Assets</h6>
                        <h4 class="mb-0">{{ metrics.assignedAssets || 0 }}</h4>
                        <span class="text-success font-size-12">Currently in use</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Available Assets -->
              <div class="col-xl-3 col-md-6 mb-4">
                <div class="card border-left-info h-100 py-2">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-shrink-0 me-3">
                        <div class="avatar-sm">
                          <span class="avatar-title bg-info rounded-circle text-white font-size-20">
                            <i class="mdi mdi-package-variant font-size-24"></i>
                          </span>
                        </div>
                      </div>
                      <div class="flex-grow-1">
                        <h6 class="font-size-15 mb-1">Available Assets</h6>
                        <h4 class="mb-0">{{ metrics.availableAssets || 0 }}</h4>
                        <span class="text-info font-size-12">Ready for assignment</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Under Maintenance -->
              <div class="col-xl-3 col-md-6 mb-4">
                <div class="card border-left-warning h-100 py-2">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-shrink-0 me-3">
                        <div class="avatar-sm">
                          <span class="avatar-title bg-warning rounded-circle text-white font-size-20">
                            <i class="mdi mdi-tools font-size-24"></i>
                          </span>
                        </div>
                      </div>
                      <div class="flex-grow-1">
                        <h6 class="font-size-15 mb-1">Under Maintenance</h6>
                        <h4 class="mb-0">{{ metrics.maintenanceAssets || 0 }}</h4>
                        <span class="text-warning font-size-12">Being repaired/serviced</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>


            <!-- Filters and controls section -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex flex-wrap align-items-center mb-3">
                      <h5 class="card-title me-3 mb-3 mb-md-0">Asset Filters</h5>
                      <div class="ms-auto">
                        <button @click="createAsset" class="btn btn-primary me-2">
                          <i class="mdi mdi-plus me-1"></i> Add New Asset
                        </button>
                        <button @click="refreshData" class="btn btn-secondary me-2">
                          <i class="mdi mdi-refresh me-1"></i> Refresh
                        </button>
                        <button @click="exportAssets" class="btn btn-success">
                          <i class="mdi mdi-file-export me-1"></i> Export
                        </button>
                      </div>
                    </div>

                    <div class="row g-3">
                      <div class="col-md-3">
                        <label class="form-label">Asset Type</label>
                        <select v-model="filters.assetType" class="form-select">
                          <option value="">All Asset Types</option>
                          <option v-for="type in assetTypes" :key="type" :value="type">
                            {{ type }}
                          </option>
                        </select>
                      </div>

                      <div class="col-md-3">
                        <label class="form-label">Department</label>
                        <select v-model="filters.departmentId" class="form-select">
                          <option value="">All Departments</option>
                          <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                            {{ dept.departmentName }}
                          </option>
                        </select>
                      </div>

                      <div class="col-md-3">
                        <label class="form-label">Status</label>
                        <select v-model="filters.status" class="form-select">
                          <option value="">All Statuses</option>
                          <option value="Active">Active</option>
                          <option value="Assigned">Assigned</option>
                          <option value="Under Maintenance">Under Maintenance</option>
                          <option value="Disposed">Disposed</option>
                        </select>
                      </div>

                      <div class="col-md-3">
                        <label class="form-label">Search</label>
                        <div class="position-relative">
                          <input
                              type="text"
                              v-model="filters.search"
                              class="form-control"
                              placeholder="Search by name, tag, employee..."
                          >
                          <i class="bx bx-search-alt search-icon position-absolute top-50 end-0 translate-middle-y me-2"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>


            <!-- Assets list table -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title">Assets List</h5>
                  </div>
                  <div class="card-body">
                    <!-- Loading overlay -->
                    <div v-if="loading" class="text-center p-5">
                      <div class="spinner-border text-primary me-2" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                      <p class="mt-2">Loading assets data...</p>
                    </div>

                    <!-- Empty state -->
                    <div v-else-if="!assets.length" class="text-center p-5">
                      <i class="mdi mdi-database-remove font-size-48 text-muted"></i>
                      <h5 class="mt-2">No assets found</h5>
                      <p class="text-muted">Try adjusting your filters or add a new asset</p>
                      <button @click="resetFilters" class="btn btn-sm btn-outline-primary mt-2">Reset Filters</button>
                    </div>

                    <!-- Assets table -->
                    <div v-else class="table-responsive">
                      <table class="table table-hover table-bordered align-middle mb-0">
                        <thead class="table-light">
                        <tr>
                          <th>Asset</th>
                          <th>Type</th>
                          <th>Department</th>
                          <th>Assigned To</th>
                          <th>Purchase Date</th>
                          <th>Status</th>
                          <th class="text-center">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="asset in assets" :key="asset.id">
                          <td>
                            <div class="d-flex align-items-center">
                              <div class="avatar-xs me-2 bg-light rounded p-1 text-center">
                                <i :class="getAssetIcon(asset.assetType)" class="font-size-18 text-primary"></i>
                              </div>
                              <div>
                                <h5 class="font-size-14 mb-0">{{ asset.name }}</h5>
                                <p class="text-muted mb-0 font-size-12">{{ asset.tagNumber }}</p>
                              </div>
                            </div>
                          </td>
                          <td>{{ asset.assetType }}</td>
                          <td>{{ asset.departmentName }}</td>
                          <td>
                            <div v-if="asset.assignedTo">
                              <span class="d-block">{{ asset.assignedToName }}</span>
                              <span class="text-muted small">{{ formatDate(asset.assignedDate) }}</span>
                            </div>
                            <span v-else class="badge bg-light text-dark">Unassigned</span>
                          </td>
                          <td>{{ formatDate(asset.purchaseDate) }}</td>
                          <td>
                            <span class="badge" :class="getStatusBadgeClass(asset.status)">
                              {{ asset.status }}
                            </span>
                          </td>
                          <td class="text-center">
                            <div class="d-flex justify-content-center">
                              <button
                                  @click="viewAssetDetails(asset)"
                                  class="btn btn-sm btn-info me-2"
                                  title="View Details"
                              >
                                <i class="mdi mdi-eye"></i>
                              </button>
                              <button
                                  @click="editAsset(asset)"
                                  class="btn btn-sm btn-primary me-2"
                                  title="Edit Asset"
                              >
                                <i class="mdi mdi-pencil"></i>
                              </button>
                              <button
                                  v-if="asset.status !== 'Assigned'"
                                  @click="assignAsset(asset)"
                                  class="btn btn-sm btn-success me-2"
                                  title="Assign Asset"
                              >
                                <i class="mdi mdi-account-check"></i>
                              </button>
                              <button
                                  v-else
                                  @click="unassignAsset(asset)"
                                  class="btn btn-sm btn-warning me-2"
                                  title="Unassign Asset"
                              >
                                <i class="mdi mdi-account-remove"></i>
                              </button>
                              <button
                                  v-if="asset.status !== 'Disposed'"
                                  @click="disposeAsset(asset)"
                                  class="btn btn-sm btn-danger"
                                  title="Dispose Asset"
                              >
                                <i class="mdi mdi-delete"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- Pagination -->
                    <div v-if="assets.length" class="d-flex justify-content-between align-items-center mt-4">
                      <div>
                        Showing <span class="fw-medium">{{ assets.length }}</span> of
                        <span class="fw-medium">{{ pagination.totalAssets }}</span> assets
                      </div>
                      <nav v-if="pagination.totalPages > 1">
                        <ul class="pagination pagination-rounded mb-0">
                          <li class="page-item" :class="{ disabled: pagination.currentPage <= 1 }">
                            <a class="page-link" href="javascript:void(0)" @click="changePage(pagination.currentPage - 1)">
                              <i class="mdi mdi-chevron-left"></i>
                            </a>
                          </li>
                          <li
                              v-for="page in getPageNumbers()"
                              :key="page"
                              class="page-item"
                              :class="{ active: page === pagination.currentPage, disabled: page === '...' }"
                          >
                            <a v-if="page !== '...'" class="page-link" href="javascript:void(0)" @click="changePage(page)">
                              {{ page }}
                            </a>
                            <span v-else class="page-link">{{ page }}</span>
                          </li>
                          <li class="page-item" :class="{ disabled: pagination.currentPage >= pagination.totalPages }">
                            <a class="page-link" href="javascript:void(0)" @click="changePage(pagination.currentPage + 1)">
                              <i class="mdi mdi-chevron-right"></i>
                            </a>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>


            <!-- Asset Type Breakdown and Recent Activity -->
            <div class="row">
              <!-- Asset Type Breakdown -->
              <div class="col-xl-6 col-lg-6 mb-4">
                <div class="card h-100">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title mb-0">Assets by Type</h5>
                  </div>
                  <div class="card-body">
                    <div v-if="metrics.typeCounts && Object.keys(metrics.typeCounts).length > 0" class="chart-container" style="position: relative; height:250px; width:100%">
                      <div class="table-responsive">
                        <table class="table table-sm table-bordered">
                          <thead class="table-light">
                          <tr>
                            <th>Asset Type</th>
                            <th class="text-center">Count</th>
                            <th class="text-center">Percentage</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="(count, type) in metrics.typeCounts" :key="type">
                            <td>{{ type }}</td>
                            <td class="text-center">{{ count }}</td>
                            <td class="text-center">
                              <div class="progress" style="height: 6px;">
                                <div
                                    class="progress-bar"
                                    :class="getAssetTypeColor(type)"
                                    role="progressbar"
                                    :style="`width: ${getAssetTypePercentage(type)}%`"
                                    :aria-valuenow="getAssetTypePercentage(type)"
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                ></div>
                              </div>
                              {{ getAssetTypePercentage(type) }}%
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div v-else class="text-center p-4">
                      <i class="mdi mdi-chart-bar font-size-48 text-muted"></i>
                      <p class="mt-2">No asset type data available</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Recent Activities -->
              <div class="col-xl-6 col-lg-6 mb-4">
                <div class="card h-100">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title mb-0">Recent Activities</h5>
                  </div>
                  <div class="card-body">
                    <div v-if="recentActivities.length > 0" class="activity-list">
                      <div v-for="activity in recentActivities" :key="activity.id" class="activity-item mb-3">
                        <div class="d-flex align-items-start p-3 border rounded">
                          <div class="activity-icon me-3 text-center">
                            <span class="avatar-title rounded-circle" :class="getActivityIconClass(activity.type)">
                              <i :class="getActivityIcon(activity.type)"></i>
                            </span>
                          </div>
                          <div class="activity-details flex-grow-1">
                            <h6 class="mb-1">{{ activity.title }}</h6>
                            <p class="text-muted mb-1 small">{{ activity.description }}</p>
                            <div class="activity-meta text-muted small">
                              <span><i class="mdi mdi-clock-outline me-1"></i>{{ formatDate(activity.date) }}</span>
                              <span class="ms-2"><i class="mdi mdi-account me-1"></i>{{ activity.user }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-center p-4">
                      <i class="mdi mdi-history font-size-48 text-muted"></i>
                      <p class="mt-2">No recent activities</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
    </div>

  <!-- Asset Create/Edit Modal -->
  <Teleport to="body">
  <div class="modal fade" id="assetModal" tabindex="-1" aria-labelledby="assetModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="assetModalLabel">{{ isEditMode ? 'Edit Asset' : 'Add New Asset' }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submitAsset">
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label">Asset Name <span class="text-danger">*</span></label>
                <input v-model="assetForm.name" type="text" class="form-control" required>
              </div>

              <div class="col-md-6">
                <label class="form-label">Asset Type <span class="text-danger">*</span></label>
                <select v-model="assetForm.assetType" class="form-select" required>
                  <option value="" disabled>Select Asset Type</option>
                  <option v-for="type in assetTypes" :key="type" :value="type">{{ type }}</option>
                </select>
              </div>

              <div class="col-md-6">
                <label class="form-label">Tag Number <span class="text-danger">*</span></label>
                <input v-model="assetForm.tagNumber" type="text" class="form-control" required>
              </div>

              <div class="col-md-6">
                <label class="form-label">Serial Number</label>
                <input v-model="assetForm.serialNumber" type="text" class="form-control">
              </div>

              <div class="col-md-6">
                <label class="form-label">Purchase Date <span class="text-danger">*</span></label>
                <input v-model="assetForm.purchaseDate" type="date" class="form-control" required>
              </div>

              <div class="col-md-6">
                <label class="form-label">Purchase Cost <span class="text-danger">*</span></label>
                <input v-model="assetForm.purchaseCost" type="number" step="0.01" class="form-control" required>
              </div>

              <div class="col-md-6">
                <label class="form-label">Department</label>
                <select v-model="assetForm.departmentId" class="form-select">
                  <option value="">None</option>
                  <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                    {{ dept.departmentName }}
                  </option>
                </select>
              </div>

              <div class="col-md-6">
                <label class="form-label">Status <span class="text-danger">*</span></label>
                <select v-model="assetForm.status" class="form-select" required>
                  <option value="Active">Active</option>
                  <option value="Under Maintenance">Under Maintenance</option>
                  <option value="Disposed">Disposed</option>
                  <option value="Assigned">Assigned</option>
                </select>
              </div>

              <div class="col-md-6">
                <label class="form-label">Warranty Expiry Date</label>
                <input v-model="assetForm.warrantyExpiry" type="date" class="form-control">
              </div>

              <div class="col-md-6">
                <label class="form-label">Supplier</label>
                <input v-model="assetForm.supplier" type="text" class="form-control">
              </div>

              <div class="col-md-12">
                <label class="form-label">Notes</label>
                <textarea v-model="assetForm.notes" class="form-control" rows="3"></textarea>
              </div>

              <div class="col-md-12">
                <label class="form-label">Documents</label>
                <input type="file" class="form-control" multiple>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          <button type="button" class="btn btn-primary" @click="submitAsset">
            {{ isEditMode ? 'Update Asset' : 'Save Asset' }}
          </button>
        </div>
      </div>
    </div>
  </div>
  </Teleport>

  <!-- Asset Details Modal -->
  <div class="modal fade" id="assetDetailsModal" tabindex="-1" aria-labelledby="assetDetailsModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="assetDetailsModalLabel">Asset Details</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="selectedAsset">
            <div class="row mb-4">
              <div class="col-md-8">
                <h4 class="font-size-16">{{ selectedAsset.name }}</h4>
                <p class="text-muted mb-1">
                  <span class="badge rounded-pill" :class="getAssetTypeColor(selectedAsset.assetType)">
                    {{ selectedAsset.assetType }}
                  </span>
                </p>
                <p class="text-muted mb-0">Tag Number: {{ selectedAsset.tagNumber }}</p>
              </div>
              <div class="col-md-4 text-md-end">
                <span class="badge" :class="getStatusBadgeClass(selectedAsset.status)">
                  {{ selectedAsset.status }}
                </span>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="card border">
                  <div class="card-header bg-light">
                    <h6 class="mb-0">Asset Information</h6>
                  </div>
                  <div class="card-body">
                    <ul class="list-group list-group-flush">
                      <li class="list-group-item">
                        <div class="d-flex justify-content-between">
                          <span><i class="mdi mdi-barcode me-2"></i>Serial Number:</span>
                          <span class="text-muted">{{ selectedAsset.serialNumber || 'N/A' }}</span>
                        </div>
                      </li>
                      <li class="list-group-item">
                        <div class="d-flex justify-content-between">
                          <span><i class="mdi mdi-calendar me-2"></i>Purchase Date:</span>
                          <span class="text-muted">{{ formatDate(selectedAsset.purchaseDate) }}</span>
                        </div>
                      </li>
                      <li class="list-group-item">
                        <div class="d-flex justify-content-between">
                          <span><i class="mdi mdi-currency-usd me-2"></i>Purchase Cost:</span>
                          <span class="text-muted">{{ formatCurrency(selectedAsset.purchaseCost,selectedAsset.currency) }}</span>
                        </div>
                      </li>
                      <li class="list-group-item">
                        <div class="d-flex justify-content-between">
                          <span><i class="mdi mdi-shield-check me-2"></i>Warranty Until:</span>
                          <span class="text-muted">{{ formatDate(selectedAsset.warrantyExpiry) || 'N/A' }}</span>
                        </div>
                      </li>
                      <li class="list-group-item">
                        <div class="d-flex justify-content-between">
                          <span><i class="mdi mdi-office-building me-2"></i>Department:</span>
                          <span class="text-muted">{{ selectedAsset.departmentName || 'N/A' }}</span>
                        </div>
                      </li>
                      <li class="list-group-item">
                        <div class="d-flex justify-content-between">
                          <span><i class="mdi mdi-store me-2"></i>Supplier:</span>
                          <span class="text-muted">{{ selectedAsset.supplier || 'N/A' }}</span>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="card border h-100">
                  <div class="card-header bg-light">
                    <h6 class="mb-0">Assignment Information</h6>
                  </div>
                  <div class="card-body">
                    <div v-if="selectedAsset.assignedTo">
                      <div class="text-center mb-3">
                        <div class="avatar avatar-xl mx-auto">
                          <span class="avatar-title rounded-circle bg-primary text-white font-size-24">
                            {{ getInitials(selectedAsset.assignedToName) }}
                          </span>
                        </div>
                        <h5 class="mt-2 mb-1">{{ selectedAsset.assignedToName }}</h5>
                        <p class="text-muted small">{{ selectedAsset.assignedToDepartment }}</p>
                      </div>
                      <ul class="list-group list-group-flush">
                        <li class="list-group-item">
                          <div class="d-flex justify-content-between">
                            <span><i class="mdi mdi-calendar-check me-2"></i>Assigned Date:</span>
                            <span class="text-muted">{{ formatDate(selectedAsset.assignedDate) }}</span>
                          </div>
                        </li>
                        <li class="list-group-item">
                          <div class="d-flex justify-content-between">
                            <span><i class="mdi mdi-account me-2"></i>Assigned By:</span>
                            <span class="text-muted">{{ selectedAsset.assignedBy || 'N/A' }}</span>
                          </div>
                        </li>
                        <li class="list-group-item">
                          <div class="d-flex justify-content-between">
                            <span><i class="mdi mdi-note-text me-2"></i>Notes:</span>
                          </div>
                        </li>
                        <li class="list-group-item">
                          <p class="text-muted mb-0">{{ selectedAsset.assignmentNotes || 'No notes provided' }}</p>
                        </li>
                      </ul>
                    </div>
                    <div v-else class="text-center py-4">
                      <i class="mdi mdi-account-off font-size-48 text-muted"></i>
                      <p class="mt-2">This asset is not assigned to any employee</p>
                      <button
                          @click="assignAsset(selectedAsset)"
                          class="btn btn-sm btn-success mt-2"
                      >
                        <i class="mdi mdi-account-check me-1"></i> Assign Now
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Maintenance History Section -->
            <div class="row mt-4" v-if="selectedAsset && selectedAsset.maintenanceHistory && selectedAsset.maintenanceHistory.length > 0">
              <div class="col-md-12">
                <div class="card border">
                  <div class="card-header bg-light">
                    <h6 class="mb-0">Maintenance History</h6>
                  </div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-sm table-bordered">
                        <thead class="table-light">
                        <tr>
                          <th>Date</th>
                          <th>Type</th>
                          <th>Description</th>
                          <th>Cost</th>
                          <th>Performed By</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(maintenance, index) in selectedAsset.maintenanceHistory" :key="index">
                          <td>{{ formatDate(maintenance.date) }}</td>
                          <td>{{ maintenance.type }}</td>
                          <td>{{ maintenance.description }}</td>
                          <td>{{ formatCurrency(maintenance.cost) }}</td>
                          <td>{{ maintenance.performedBy }}</td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Asset History Section -->
            <div class="row mt-4">
              <div class="col-md-12">
                <div class="card border">
                  <div class="card-header bg-light d-flex justify-content-between align-items-center">
                    <h6 class="mb-0">Asset History</h6>
                  </div>
                  <div class="card-body">
                    <div v-if="selectedAsset.history && selectedAsset.history.length > 0">
                      <div class="timeline-container">
                        <div v-for="(event, index) in selectedAsset.history" :key="index" class="timeline-item mb-3">
                          <div class="timeline-marker" :class="getTimelineMarkerClass(event.type)">
                            <i :class="getTimelineIcon(event.type)"></i>
                          </div>
                          <div class="timeline-content">
                            <h6 class="mb-1">{{ event.title }}</h6>
                            <p class="mb-1 text-muted">{{ event.description }}</p>
                            <small class="text-muted">
                              <i class="mdi mdi-calendar me-1"></i>{{ formatDateTime(event.timestamp) }}
                              <span class="ms-2"><i class="mdi mdi-account me-1"></i>{{ event.user }}</span>
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-center py-4">
                      <i class="mdi mdi-history font-size-48 text-muted"></i>
                      <p class="mt-2">No history available for this asset</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          <button type="button" class="btn btn-primary" @click="editAsset(selectedAsset)">
            <i class="mdi mdi-pencil me-1"></i> Edit
          </button>
          <button type="button" class="btn btn-info" @click="exportAssetDetails(selectedAsset)">
            <i class="mdi mdi-download me-1"></i> Export
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Assign Asset Modal -->
  <div class="modal fade" id="assignAssetModal" tabindex="-1" aria-labelledby="assignAssetModalLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="assignAssetModalLabel">Assign Asset</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="selectedAsset" class="mb-3">
            <p class="mb-3">
              You are assigning <strong>{{ selectedAsset.name }}</strong>
              <span class="badge" :class="getAssetTypeColor(selectedAsset.assetType)">{{ selectedAsset.assetType }}</span>
            </p>

            <div class="mb-3">
              <label class="form-label">Assign To Employee <span class="text-danger">*</span></label>
              <select v-model="assignmentForm.employeeId" class="form-select" required>
                <option value="" disabled>Select Employee</option>
                <option v-for="employee in employees" :key="employee.id" :value="employee.id">
                  {{ employee.firstName }} {{ employee.lastName }} - {{ employee.departmentName }}
                </option>
              </select>
            </div>

            <div class="mb-3">
              <label class="form-label">Assignment Date <span class="text-danger">*</span></label>
              <input v-model="assignmentForm.assignmentDate" type="date" class="form-control" required>
            </div>

            <div class="mb-3">
              <label class="form-label">Notes</label>
              <textarea v-model="assignmentForm.notes" class="form-control" rows="3" placeholder="Add additional notes about this assignment..."></textarea>
            </div>

            <div class="form-check mb-3">
              <input
                  class="form-check-input"
                  type="checkbox"
                  v-model="assignmentForm.notifyEmployee"
                  id="notifyEmployeeCheck"
              >
              <label class="form-check-label" for="notifyEmployeeCheck">
                Send notification to employee
              </label>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
          <button type="button" class="btn btn-success" @click="confirmAssignAsset">
            <i class="mdi mdi-account-check me-1"></i> Assign Asset
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Unassign Asset Modal -->
  <div class="modal fade" id="unassignAssetModal" tabindex="-1" aria-labelledby="unassignAssetModalLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="unassignAssetModalLabel">Unassign Asset</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="selectedAsset">
            <p class="mb-3">
              Are you sure you want to unassign <strong>{{ selectedAsset.name }}</strong> from <strong>{{ selectedAsset.assignedToName }}</strong>?
            </p>

            <div class="alert alert-warning">
              <i class="mdi mdi-alert me-2"></i>
              This will remove the asset from the employee's inventory and make it available for reassignment.
            </div>

            <div class="mb-3">
              <label class="form-label">Return Date <span class="text-danger">*</span></label>
              <input v-model="unassignmentForm.returnDate" type="date" class="form-control" required>
            </div>

            <div class="mb-3">
              <label class="form-label">Return Condition</label>
              <select v-model="unassignmentForm.returnCondition" class="form-select">
                <option value="Excellent">Excellent</option>
                <option value="Good">Good</option>
                <option value="Fair">Fair</option>
                <option value="Poor">Poor</option>
                <option value="Damaged">Damaged</option>
              </select>
            </div>

            <div class="mb-3">
              <label class="form-label">Notes</label>
              <textarea v-model="unassignmentForm.notes" class="form-control" rows="3" placeholder="Add notes about the return condition or reason..."></textarea>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
          <button type="button" class="btn btn-warning" @click="confirmUnassignAsset">
            <i class="mdi mdi-account-remove me-1"></i> Unassign Asset
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Dispose Asset Modal -->
  <div class="modal fade" id="disposeAssetModal" tabindex="-1" aria-labelledby="disposeAssetModalLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="disposeAssetModalLabel">Dispose Asset</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="selectedAsset">
            <p class="mb-3">
              Are you sure you want to mark <strong>{{ selectedAsset.name }}</strong> as disposed?
            </p>

            <div class="alert alert-danger">
              <i class="mdi mdi-alert-circle me-2"></i>
              This action will update the asset status to "Disposed" and cannot be undone.
            </div>

            <div class="mb-3">
              <label class="form-label">Disposal Date <span class="text-danger">*</span></label>
              <input v-model="disposalForm.disposalDate" type="date" class="form-control" required>
            </div>

            <div class="mb-3">
              <label class="form-label">Disposal Method <span class="text-danger">*</span></label>
              <select v-model="disposalForm.disposalMethod" class="form-select" required>
                <option value="" disabled>Select Method</option>
                <option value="Sold">Sold</option>
                <option value="Recycled">Recycled</option>
                <option value="Donated">Donated</option>
                <option value="Discarded">Discarded</option>
                <option value="Returned to Supplier">Returned to Supplier</option>
              </select>
            </div>

            <div class="mb-3" v-if="disposalForm.disposalMethod === 'Sold'">
              <label class="form-label">Sale Amount</label>
              <input v-model="disposalForm.saleAmount" type="number" step="0.01" class="form-control">
            </div>

            <div class="mb-3">
              <label class="form-label">Reason for Disposal <span class="text-danger">*</span></label>
              <textarea v-model="disposalForm.reason" class="form-control" rows="3" required></textarea>
            </div>

            <div class="mb-3">
              <label class="form-label">Authorized By</label>
              <input v-model="disposalForm.authorizedBy" type="text" class="form-control">
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
          <button type="button" class="btn btn-danger" @click="confirmDisposeAsset">
            <i class="mdi mdi-delete me-1"></i> Dispose Asset
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Maintenance Modal -->
  <div class="modal fade" id="maintenanceModal" tabindex="-1" aria-labelledby="maintenanceModalLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="maintenanceModalLabel">Add Maintenance Record</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="selectedAsset">
            <p class="mb-3">
              Recording maintenance for <strong>{{ selectedAsset.name }}</strong>
            </p>

            <div class="mb-3">
              <label class="form-label">Maintenance Date <span class="text-danger">*</span></label>
              <input v-model="maintenanceForm.date" type="date" class="form-control" required>
            </div>

            <div class="mb-3">
              <label class="form-label">Maintenance Type <span class="text-danger">*</span></label>
              <select v-model="maintenanceForm.type" class="form-select" required>
                <option value="" disabled>Select Type</option>
                <option value="Repair">Repair</option>
                <option value="Scheduled Maintenance">Scheduled Maintenance</option>
                <option value="Upgrade">Upgrade</option>
                <option value="Cleaning">Cleaning</option>
                <option value="Inspection">Inspection</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div class="mb-3">
              <label class="form-label">Description <span class="text-danger">*</span></label>
              <textarea v-model="maintenanceForm.description" class="form-control" rows="3" required></textarea>
            </div>

            <div class="mb-3">
              <label class="form-label">Cost</label>
              <input v-model="maintenanceForm.cost" type="number" step="0.01" class="form-control">
            </div>

            <div class="mb-3">
              <label class="form-label">Performed By</label>
              <input v-model="maintenanceForm.performedBy" type="text" class="form-control">
            </div>

            <div class="mb-3">
              <label class="form-label">Status After Maintenance</label>
              <select v-model="maintenanceForm.newStatus" class="form-select">
                <option value="Active">Active</option>
                <option value="Under Maintenance">Still Under Maintenance</option>
              </select>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
          <button type="button" class="btn btn-primary" @click="confirmAddMaintenance">
            <i class="mdi mdi-tools me-1"></i> Save Maintenance Record
          </button>
        </div>
      </div>
    </div>
  </div>
  </HrPageLayout>
</template>

<style scoped>
/* Timeline styling */
.timeline-container {
  position: relative;
  padding-left: 2rem;
}

.timeline-item {
  position: relative;
  padding-bottom: 1.5rem;
}

.timeline-marker {
  position: absolute;
  left: -15px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  background-color: #4e73df;
}

.timeline-marker.bg-success {
  background-color: #1cc88a;
}

.timeline-marker.bg-danger {
  background-color: #e74a3b;
}

.timeline-marker.bg-warning {
  background-color: #f6c23e;
}

.timeline-marker.bg-info {
  background-color: #36b9cc;
}

.timeline-content {
  background-color: #f8f9fc;
  padding: 0.75rem;
  border-radius: 0.35rem;
  margin-left: 1rem;
  border-left: 3px solid #4e73df;
}

.border-left-primary {
  border-left: 4px solid #4e73df !important;
}

.border-left-success {
  border-left: 4px solid #1cc88a !important;
}

.border-left-info {
  border-left: 4px solid #36b9cc !important;
}

.border-left-warning {
  border-left: 4px solid #f6c23e !important;
}

.avatar-xl {
  width: 6rem;
  height: 6rem;
  font-size: 2.5rem;
}
</style>

<script>
/* eslint-disable */
import { Modal } from "bootstrap";
import { authUrl, execute } from "@/api";
import _ from 'lodash';
import HrPageLayout from "./HrPageLayout.vue";
import "@/assets/css/hr-page-shared.css";

export default {
  name: "Assets",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      assets: [],
      departments: [],
      employees: [],
      assetTypes: [
        'Computer',
        'Laptop',
        'Mobile Phone',
        'Tablet',
        'Monitor',
        'Printer',
        'Furniture',
        'Vehicle',
        'Boats',
        'Office Equipment',
        'Software',
        'Other'
      ],
      filters: {
        assetType: "",
        departmentId: "",
        status: "",
        search: "",
        page: 1,
        limit: 10
      },
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalAssets: 0,
        limit: 10
      },
      metrics: {
        totalAssets: 0,
        assignedAssets: 0,
        availableAssets: 0,
        maintenanceAssets: 0,
        typeCounts: {}
      },
      selectedAsset: null,
      assetForm: {
        name: "",
        assetType: "",
        tagNumber: "",
        serialNumber: "",
        purchaseDate: "",
        purchaseCost: "",
        departmentId: "",
        status: "Active",
        warrantyExpiry: "",
        supplier: "",
        notes: ""
      },
      assignmentForm: {
        employeeId: "",
        assignmentDate: new Date().toISOString().split('T')[0],
        notes: "",
        notifyEmployee: true
      },
      unassignmentForm: {
        returnDate: new Date().toISOString().split('T')[0],
        returnCondition: "Good",
        notes: ""
      },
      disposalForm: {
        disposalDate: new Date().toISOString().split('T')[0],
        disposalMethod: "",
        saleAmount: 0,
        reason: "",
        authorizedBy: ""
      },
      maintenanceForm: {
        date: new Date().toISOString().split('T')[0],
        type: "",
        description: "",
        cost: 0,
        performedBy: "",
        newStatus: "Active"
      },
      isEditMode: false,
      recentActivities: []
    };
  },
  mounted() {
    this.getDepartments();
    this.getEmployees();
    this.getAssets();
    this.getRecentActivities();
  },
  methods: {
    // Core data fetching methods
    getAssets() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getAssets");

      // Add filter parameters
      Object.keys(this.filters).forEach(key => {
        if (this.filters[key]) {
          data.append(key, this.filters[key]);
        }
      });

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              this.assets = res.data.data.assets || [];
              this.metrics = res.data.data.metrics || {};
              this.pagination = res.data.data.pagination || {
                currentPage: 1,
                totalPages: 1,
                totalAssets: 0,
                limit: 10
              };
            } else {
              console.error("Error fetching assets:", res.data.message);
              this.showToast("error", "Failed to load assets data: " + res.data.message);
              this.assets = [];
              this.metrics = {};
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching assets");
            this.assets = [];
            this.metrics = {};
          });
    },

    getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.departments = res.data.data.departments || [];
            } else {
              console.error("Error fetching departments:", res.data.message);
              this.showToast("error", "Failed to load departments");
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching departments");
          });
    },

    getEmployees() {
      const data = new FormData();
      data.append("function", "getEmployees");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.employees = res.data.data.employees || [];
            } else {
              console.error("Error fetching employees:", res.data.message);
              this.showToast("error", "Failed to load employees");
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching employees");
          });
    },

    getRecentActivities() {
      const data = new FormData();
      data.append("function", "getAssetActivities");
      data.append("limit", 5);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.recentActivities = res.data.data.activities || [];
            } else {
              console.error("Error fetching asset activities:", res.data.message);
              this.recentActivities = [];
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.recentActivities = [];
          });
    },

    // UI interaction methods
    refreshData() {
      this.getAssets();
      this.getRecentActivities();
    },

    resetFilters() {
      this.filters = {
        assetType: "",
        departmentId: "",
        status: "",
        search: "",
        page: 1,
        limit: 10
      };
      this.getAssets();
    },

    changePage(page) {
      if (page < 1 || page > this.pagination.totalPages || page === this.filters.page) {
        return;
      }

      this.filters.page = page;
      this.getAssets();
    },

    getPageNumbers() {
      const totalPages = this.pagination.totalPages || 1;
      const currentPage = this.pagination.currentPage || 1;

      if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }

      const pages = [1];

      if (currentPage <= 3) {
        pages.push(2, 3);
        if (totalPages > 3) pages.push('...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push('...', totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push('...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }

      return pages;
    },

    // Asset CRUD operations
    createAsset() {
      this.isEditMode = false;
      this.assetForm = {
        name: "",
        assetType: "",
        tagNumber: "",
        serialNumber: "",
        purchaseDate: new Date().toISOString().split('T')[0],
        purchaseCost: "",
        departmentId: "",
        status: "Active",
        warrantyExpiry: "",
        supplier: "",
        notes: ""
      };
      const modalElement = document.getElementById('assetModal');
      if (modalElement) Modal.getOrCreateInstance(modalElement).show();
    },

    editAsset(asset) {
      this.isEditMode = true;
      this.selectedAsset = asset;

      // Close details modal if open
      const detailsModal = Modal.getInstance(document.getElementById('assetDetailsModal'));
      if (detailsModal) {
        detailsModal.hide();
      }

      // Prepare form data
      this.assetForm = {
        id: asset.id,
        name: asset.name,
        assetType: asset.assetType,
        tagNumber: asset.tagNumber,
        serialNumber: asset.serialNumber || "",
        purchaseDate: asset.purchaseDate,
        purchaseCost: asset.purchaseCost,
        departmentId: asset.departmentId || "",
        status: asset.status,
        warrantyExpiry: asset.warrantyExpiry || "",
        supplier: asset.supplier || "",
        notes: asset.notes || ""
      };

      // Open the asset modal
      const modalElement = document.getElementById('assetModal');
      if (modalElement) Modal.getOrCreateInstance(modalElement).show();
    },

    submitAsset() {
      // Validate required fields
      if (!this.assetForm.name || !this.assetForm.assetType || !this.assetForm.tagNumber || !this.assetForm.purchaseDate || !this.assetForm.purchaseCost) {
        this.showToast("error", "Please fill in all required fields");
        return;
      }

      // Prepare form data
      const data = new FormData();

      if (this.isEditMode) {
        data.append("function", "updateAsset");
        data.append("assetId", this.assetForm.id);
      } else {
        data.append("function", "createAsset");
      }

      // Add all form fields
      data.append("name", this.assetForm.name);
      data.append("assetType", this.assetForm.assetType);
      data.append("tagNumber", this.assetForm.tagNumber);
      data.append("serialNumber", this.assetForm.serialNumber);
      data.append("purchaseDate", this.assetForm.purchaseDate);
      data.append("purchaseCost", this.assetForm.purchaseCost);
      data.append("departmentId", this.assetForm.departmentId);
      data.append("status", this.assetForm.status);
      data.append("warrantyExpiry", this.assetForm.warrantyExpiry);
      data.append("supplier", this.assetForm.supplier);
      data.append("notes", this.assetForm.notes);

      // Send the request
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.showToast("success", this.isEditMode ? "Asset updated successfully" : "Asset created successfully");

              // Close the modal
              const modal = Modal.getInstance(document.getElementById('assetModal'));
              if (modal) {
                modal.hide();
              }

              // Refresh the assets data
              this.getAssets();
              this.getRecentActivities();
            } else {
              console.error("Error saving asset:", res.data.message);
              this.showToast("error", `Failed to ${this.isEditMode ? 'update' : 'create'} asset: ${res.data.message}`);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", `Network error when ${this.isEditMode ? 'updating' : 'creating'} asset`);
          });
    },

    viewAssetDetails(asset) {
      this.selectedAsset = null;
      this.getAssetDetails(asset.id);
    },

    getAssetDetails(assetId) {
      const data = new FormData();
      data.append("function", "getAssetDetails");
      data.append("assetId", assetId);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.selectedAsset = res.data.data.selectedAsset;

              // Open the asset details modal
              // Open the asset details modal
              Modal.getOrCreateInstance(document.getElementById('assetDetailsModal')).show();
            } else {
              console.error("Error fetching asset details:", res.data.message);
              this.showToast("error", "Failed to load asset details: " + res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching asset details");
          });
    },

    // Asset Assignment Methods
    assignAsset(asset) {
      this.selectedAsset = asset;
      this.assignmentForm = {
        employeeId: "",
        assignmentDate: new Date().toISOString().split('T')[0],
        notes: "",
        notifyEmployee: true
      };

      // Close any other open modals
      const detailsModal = Modal.getInstance(document.getElementById('assetDetailsModal'));
      if (detailsModal) {
        detailsModal.hide();
      }

      // Open the assignment modal
      Modal.getOrCreateInstance(document.getElementById('assignAssetModal')).show();
    },

    confirmAssignAsset() {
      if (!this.assignmentForm.employeeId) {
        this.showToast("error", "Please select an employee");
        return;
      }

      const data = new FormData();
      data.append("function", "assignAsset");
      data.append("assetId", this.selectedAsset.id);
      data.append("employeeId", this.assignmentForm.employeeId);
      data.append("assignmentDate", this.assignmentForm.assignmentDate);
      data.append("notes", this.assignmentForm.notes);
      data.append("notifyEmployee", this.assignmentForm.notifyEmployee ? 1 : 0);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.showToast("success", "Asset assigned successfully");

              // Close the modal
              const modal = Modal.getInstance(document.getElementById('assignAssetModal'));
              if (modal) {
                modal.hide();
              }

              // Refresh the assets data
              this.getAssets();
              this.getRecentActivities();
            } else {
              console.error("Error assigning asset:", res.data.message);
              this.showToast("error", "Failed to assign asset: " + res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when assigning asset");
          });
    },

    unassignAsset(asset) {
      this.selectedAsset = asset;
      this.unassignmentForm = {
        returnDate: new Date().toISOString().split('T')[0],
        returnCondition: "Good",
        notes: ""
      };

      // Close any other open modals
      const detailsModal = Modal.getInstance(document.getElementById('assetDetailsModal'));
      if (detailsModal) {
        detailsModal.hide();
      }

      // Open the unassignment modal
      Modal.getOrCreateInstance(document.getElementById('unassignAssetModal')).show();
    },

    confirmUnassignAsset() {
      const data = new FormData();
      data.append("function", "unassignAsset");
      data.append("assetId", this.selectedAsset.id);
      data.append("returnDate", this.unassignmentForm.returnDate);
      data.append("returnCondition", this.unassignmentForm.returnCondition);
      data.append("notes", this.unassignmentForm.notes);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.showToast("success", "Asset unassigned successfully");

              // Close the modal
              const modal = Modal.getInstance(document.getElementById('unassignAssetModal'));
              if (modal) {
                modal.hide();
              }

              // Refresh the assets data
              this.getAssets();
              this.getRecentActivities();
            } else {
              console.error("Error unassigning asset:", res.data.message);
              this.showToast("error", "Failed to unassign asset: " + res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when unassigning asset");
          });
    },

    // Asset Disposal Methods
    disposeAsset(asset) {
      this.selectedAsset = asset;
      this.disposalForm = {
        disposalDate: new Date().toISOString().split('T')[0],
        disposalMethod: "",
        saleAmount: 0,
        reason: "",
        authorizedBy: ""
      };

      // Close any other open modals
      const detailsModal = Modal.getInstance(document.getElementById('assetDetailsModal'));
      if (detailsModal) {
        detailsModal.hide();
      }

      // Open the disposal modal
      Modal.getOrCreateInstance(document.getElementById('disposeAssetModal')).show();
    },

    confirmDisposeAsset() {
      if (!this.disposalForm.disposalMethod || !this.disposalForm.reason) {
        this.showToast("error", "Please fill in all required fields");
        return;
      }

      const data = new FormData();
      data.append("function", "disposeAsset");
      data.append("assetId", this.selectedAsset.id);
      data.append("disposalDate", this.disposalForm.disposalDate);
      data.append("disposalMethod", this.disposalForm.disposalMethod);
      data.append("reason", this.disposalForm.reason);

      if (this.disposalForm.disposalMethod === 'Sold' && this.disposalForm.saleAmount > 0) {
        data.append("saleAmount", this.disposalForm.saleAmount);
      }

      data.append("authorizedBy", this.disposalForm.authorizedBy);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.showToast("success", "Asset disposed successfully");

              // Close the modal
              const modal = Modal.getInstance(document.getElementById('disposeAssetModal'));
              if (modal) {
                modal.hide();
              }

              // Refresh the assets data
              this.getAssets();
              this.getRecentActivities();
            } else {
              console.error("Error disposing asset:", res.data.message);
              this.showToast("error", "Failed to dispose asset: " + res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when disposing asset");
          });
    },

    // Asset Maintenance Methods
    addMaintenanceRecord(asset) {
      this.selectedAsset = asset;
      this.maintenanceForm = {
        date: new Date().toISOString().split('T')[0],
        type: "",
        description: "",
        cost: 0,
        performedBy: "",
        newStatus: "Active"
      };

      // Close any other open modals
      const detailsModal = Modal.getInstance(document.getElementById('assetDetailsModal'));
      if (detailsModal) {
        detailsModal.hide();
      }

      // Open the maintenance modal
      Modal.getOrCreateInstance(document.getElementById('maintenanceModal')).show();
    },

    confirmAddMaintenance() {
      if (!this.maintenanceForm.type || !this.maintenanceForm.description) {
        this.showToast("error", "Please fill in all required fields");
        return;
      }

      const data = new FormData();
      data.append("function", "addAssetMaintenance");
      data.append("assetId", this.selectedAsset.id);
      data.append("date", this.maintenanceForm.date);
      data.append("type", this.maintenanceForm.type);
      data.append("description", this.maintenanceForm.description);
      data.append("cost", this.maintenanceForm.cost);
      data.append("performedBy", this.maintenanceForm.performedBy);
      data.append("newStatus", this.maintenanceForm.newStatus);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.showToast("success", "Maintenance record added successfully");

              // Close the modal
              const modal = Modal.getInstance(document.getElementById('maintenanceModal'));
              if (modal) {
                modal.hide();
              }

              // Refresh the assets data and update the selected asset details
              this.getAssets();
              this.getRecentActivities();
              if (this.selectedAsset) {
                this.getAssetDetails(this.selectedAsset.id);
              }
            } else {
              console.error("Error adding maintenance record:", res.data.message);
              this.showToast("error", "Failed to add maintenance record: " + res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when adding maintenance record");
          });
    },

    // Report and Export Methods
    exportAssets() {
      const data = new FormData();
      data.append("function", "exportAssetsReport");

      // Add filter parameters for the export
      Object.keys(this.filters).forEach(key => {
        if (this.filters[key] && key !== 'page' && key !== 'limit') {
          data.append(key, this.filters[key]);
        }
      });

      // Use vanilla form submission for file download
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = authUrl;

      // Append each key-value pair to the form
      for (const pair of data.entries()) {
        const hiddenField = document.createElement('input');
        hiddenField.type = 'hidden';
        hiddenField.name = pair[0];
        hiddenField.value = pair[1];
        form.appendChild(hiddenField);
      }

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },

    exportAssetDetails(asset) {
      const data = new FormData();
      data.append("function", "exportAssetDetails");
      data.append("assetId", asset.id);

      // Use vanilla form submission for file download
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = authUrl;

      // Append each key-value pair to the form
      for (const pair of data.entries()) {
        const hiddenField = document.createElement('input');
        hiddenField.type = 'hidden';
        hiddenField.name = pair[0];
        hiddenField.value = pair[1];
        form.appendChild(hiddenField);
      }

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },

    // Helper methods for display and formatting
    getAssetIcon(assetType) {
      const iconMap = {
        'Computer': 'mdi mdi-desktop-mac',
        'Laptop': 'mdi mdi-laptop',
        'Mobile Phone': 'mdi mdi-cellphone',
        'Tablet': 'mdi mdi-tablet',
        'Monitor': 'mdi mdi-monitor',
        'Printer': 'mdi mdi-printer',
        'Furniture': 'mdi mdi-chair-rolling',
        'Vehicle': 'mdi mdi-car',
        'Office Equipment': 'mdi mdi-tools',
        'Software': 'mdi mdi-microsoft',
        'Other': 'mdi mdi-package-variant-closed'
      };

      return iconMap[assetType] || 'mdi mdi-package-variant-closed';
    },

    getAssetTypeColor(type) {
      const colorMap = {
        'Computer': 'bg-primary',
        'Laptop': 'bg-info',
        'Mobile Phone': 'bg-success',
        'Tablet': 'bg-purple',
        'Monitor': 'bg-indigo',
        'Printer': 'bg-teal',
        'Furniture': 'bg-orange',
        'Vehicle': 'bg-warning',
        'Office Equipment': 'bg-secondary',
        'Software': 'bg-pink',
        'Other': 'bg-dark'
      };

      return colorMap[type] || 'bg-secondary';
    },

    getStatusBadgeClass(status) {
      const statusMap = {
        'Active': 'bg-success',
        'Assigned': 'bg-primary',
        'Under Maintenance': 'bg-warning',
        'Disposed': 'bg-danger',
        'Out of Order': 'bg-danger',
        'In Storage': 'bg-info'
      };

      return statusMap[status] || 'bg-secondary';
    },

    getActivityIconClass(type) {
      const typeMap = {
        'create': 'bg-primary',
        'update': 'bg-info',
        'assign': 'bg-success',
        'unassign': 'bg-warning',
        'maintenance': 'bg-purple',
        'dispose': 'bg-danger'
      };

      return typeMap[type] || 'bg-secondary';
    },

    getActivityIcon(type) {
      const iconMap = {
        'create': 'mdi mdi-plus',
        'update': 'mdi mdi-pencil',
        'assign': 'mdi mdi-account-check',
        'unassign': 'mdi mdi-account-remove',
        'maintenance': 'mdi mdi-tools',
        'dispose': 'mdi mdi-delete'
      };

      return iconMap[type] || 'mdi mdi-information';
    },

    getTimelineMarkerClass(type) {
      const typeMap = {
        'create': 'bg-primary',
        'update': 'bg-info',
        'assign': 'bg-success',
        'unassign': 'bg-warning',
        'maintenance': 'bg-info',
        'dispose': 'bg-danger'
      };

      return typeMap[type] || 'bg-secondary';
    },

    getTimelineIcon(type) {
      const iconMap = {
        'create': 'mdi mdi-plus',
        'update': 'mdi mdi-pencil',
        'assign': 'mdi mdi-account-check',
        'unassign': 'mdi mdi-account-remove',
        'maintenance': 'mdi mdi-tools',
        'dispose': 'mdi mdi-delete'
      };

      return iconMap[type] || 'mdi mdi-information';
    },

    getAssetTypePercentage(type) {
      if (!this.metrics.typeCounts || !this.metrics.totalAssets) return 0;

      const count = this.metrics.typeCounts[type] || 0;
      return ((count / this.metrics.totalAssets) * 100).toFixed(1);
    },

    getInitials(name) {
      if (!name) return '';

      return name
          .split(' ')
          .map(word => word.charAt(0).toUpperCase())
          .join('')
          .substr(0, 2);
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';

      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatDateTime(dateTimeString) {
      if (!dateTimeString) return 'N/A';

      return new Date(dateTimeString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    },

    formatCurrency(value,currency) {
      if (!value) return `${currency}0.00`;

      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency
      }).format(value);
    },

    showToast(type, message) {
      // Check if toast plugin exists before using it
      if (this.$toast) {
        this.$toast[type](message);
      } else {
        // Fallback to alert if toast plugin not available
        alert(message);
      }
    }
  },
  watch: {
    'filters.assetType': _.debounce(function() {
      this.filters.page = 1; // Reset to first page
      this.getAssets();
    }, 300),

    'filters.departmentId': _.debounce(function() {
      this.filters.page = 1; // Reset to first page
      this.getAssets();
    }, 300),

    'filters.status': _.debounce(function() {
      this.filters.page = 1; // Reset to first page
      this.getAssets();
    }, 300),

    'filters.search': _.debounce(function() {
      this.filters.page = 1; // Reset to first page
      this.getAssets();
    }, 500)
  }
};
</script>